(function () {
  'use strict';

  if (window.__SE_PREDICTIVE_SEARCH_NATIVE_REBUILT__) return;
  window.__SE_PREDICTIVE_SEARCH_NATIVE_REBUILT__ = true;

  const states = new WeakMap();

  function debounce(callback, delay) {
    let timeoutId;

    return function (...args) {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        callback.apply(this, args);
      }, delay);
    };
  }

  function getInput(wrapper) {
    return wrapper.querySelector('input[type="search"][name="q"]');
  }

  function getForm(wrapper) {
    const input = getInput(wrapper);
    return input ? input.form : null;
  }

  function getResultsContainer(wrapper) {
    return wrapper.querySelector('[data-predictive-search]');
  }

  function setExpanded(wrapper, expanded) {
    const input = getInput(wrapper);

    if (expanded) {
      wrapper.setAttribute('open', '');
      wrapper.isOpen = true;
    } else {
      wrapper.removeAttribute('open');
      wrapper.isOpen = false;
    }

    if (input) {
      input.setAttribute('aria-expanded', expanded ? 'true' : 'false');

      if (!expanded) {
        input.removeAttribute('aria-activedescendant');
      }
    }
  }

  function closeResults(wrapper, clearResults) {
    const results = getResultsContainer(wrapper);
    const state = states.get(wrapper);

    wrapper.removeAttribute('loading');
    wrapper.removeAttribute('results');
    setExpanded(wrapper, false);

    if (clearResults && results) {
      results.innerHTML = '';
    }

    if (state && state.controller) {
      state.controller.abort();
      state.controller = null;
    }
  }

  function buildRequestUrl(term) {
    const routesObject = window.routes || {};
    const endpoint = routesObject.predictive_search_url || '/search/suggest';

    const params = new URLSearchParams();
    params.set('q', term);
    params.set(
      'resources[options][fields]',
      'title,product_type,variants.title,vendor,variants.sku'
    );
    params.set('resources[limit]', '8');
    params.set('resources[limit_scope]', 'each');
    params.set('section_id', 'predictive-search');

    return `${endpoint}?${params.toString()}`;
  }

  async function fetchResults(wrapper, term) {
    const state = states.get(wrapper);
    const results = getResultsContainer(wrapper);

    if (!state || !results || !term) return;

    if (state.controller) {
      state.controller.abort();
    }

    const controller = new AbortController();
    state.controller = controller;
    state.latestTerm = term;

    wrapper.setAttribute('loading', '');
    setExpanded(wrapper, true);

    try {
      const response = await fetch(buildRequestUrl(term), {
        method: 'GET',
        signal: controller.signal,
        headers: {
          Accept: 'text/html'
        }
      });

      if (!response.ok) {
        throw new Error(`Predictive search request failed: ${response.status}`);
      }

      const responseText = await response.text();

      if (state.latestTerm !== term) return;

      const responseDocument = new DOMParser().parseFromString(
        responseText,
        'text/html'
      );

      const section = responseDocument.querySelector(
        '#shopify-section-predictive-search'
      );

      if (!section) {
        closeResults(wrapper, true);
        return;
      }

      results.innerHTML = section.innerHTML;

      wrapper.removeAttribute('loading');
      wrapper.setAttribute('results', '');
      setExpanded(wrapper, true);
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error(error);
      closeResults(wrapper, true);
    }
  }

  function initialisePredictiveSearch(wrapper) {
    if (states.has(wrapper)) return;

    const input = getInput(wrapper);
    const form = getForm(wrapper);
    const results = getResultsContainer(wrapper);

    if (!input || !form || !results) return;

    const state = {
      controller: null,
      latestTerm: '',
      debouncedSearch: null
    };

    state.debouncedSearch = debounce(function () {
      const term = input.value.trim();

      if (!term) {
        closeResults(wrapper, true);
        return;
      }

      fetchResults(wrapper, term);
    }, 180);

    states.set(wrapper, state);

    input.addEventListener('input', state.debouncedSearch);

    input.addEventListener('focus', function () {
      const term = input.value.trim();

      if (!term) return;

      if (wrapper.hasAttribute('results') && results.innerHTML.trim()) {
        setExpanded(wrapper, true);
      } else {
        fetchResults(wrapper, term);
      }
    });

    input.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeResults(wrapper, false);
        input.focus();
      }
    });

    /*
     * Critical behaviour:
     * valid searches use the browser's native GET submission.
     * No preventDefault, redirect or synthetic second submission.
     */
    form.addEventListener(
      'submit',
      function (event) {
        const term = input.value.trim();
        input.value = term;

        if (!term) {
          event.preventDefault();
          closeResults(wrapper, true);
          input.focus();
          return;
        }

        closeResults(wrapper, false);
      },
      true
    );

    wrapper.addEventListener('click', function (event) {
      const closeButton = event.target.closest(
        '.inline-search__close-button'
      );

      if (closeButton) {
        event.preventDefault();
        input.value = '';
        closeResults(wrapper, true);
        input.focus();
        return;
      }

      const link = event.target.closest('a[href]');

      if (link) {
        closeResults(wrapper, false);
      }
    });
  }

  function initialiseAll(root) {
    (root || document)
      .querySelectorAll('predictive-search')
      .forEach(initialisePredictiveSearch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initialiseAll(document);
    });
  } else {
    initialiseAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initialiseAll(event.target);
  });

  document.addEventListener(
    'click',
    function (event) {
      document
        .querySelectorAll('predictive-search[open]')
        .forEach(function (wrapper) {
          if (!wrapper.contains(event.target)) {
            closeResults(wrapper, false);
          }
        });
    },
    true
  );
})();
